/* THE DOCUMENT — what a project is, on disk and in memory.

   Pure: no DOM, no three, no S. Everything takes plain data and returns plain
   data, so the save path, the load path and the migration all share one
   definition of what a valid project looks like.

   Phase 2 bumps DOC_VERSION to 2, where every embedded data URL becomes an
   `assetId` pointing at Supabase Storage. Until then the on-disk shape is
   byte-compatible with the artifact, so existing .json files keep opening. */

/* v1  every blob is a base64 data URL sitting inside the document
   v2  every blob is an `assetId` pointing at Supabase Storage

   v1 is still what "download .json" writes when the photo is embedded, because a
   self-contained file you can email is the point of that escape hatch. v2 is
   what goes in the database. Both open. */
import { normalizeAnim } from './anim.js';
import { projectDefaults } from './defaults.js';
import { builtInWeights, resolveFamily, snapWeight } from './fonts.js';

export const DOC_VERSION = 2;
export const LEGACY_VERSION = 1;

/** A well-formed {x,y,z} with every component finite and in range, or null.
 *  Null rather than a repaired vector on purpose: for an optional field,
 *  "this was never set" and "this was set to zero" have to stay tellable
 *  apart — see o.pivot, where the two mean different things. */
function vec3In(v, lo, hi) {
  if (!v || typeof v !== 'object') return null;
  const out = {};
  for (const k of ['x', 'y', 'z']) {
    const n = +v[k];
    if (!Number.isFinite(n)) return null;
    out[k] = Math.min(hi, Math.max(lo, n));
  }
  return out;
}

/* How many screens an array may hold. Not an arbitrary tidiness limit: the
   video export opens ONE ENCODER PER SCREEN and mediabunny's BufferTarget keeps
   each finished file in memory until the ZIP is written, so the ceiling here is
   the browser's, not the geometry's. 24 covers the walls anyone builds by hand
   (6x4, 8x3) and keeps the worst case to something a laptop survives. */
export const MAX_TILES = 24;

/* ── object defaults ─────────────────────────────────────────────────────── */

export const DEF = {
  recess:   { w: 5, h: 2.6, depth: 1.8, border: .24, bevel: .09, bevelSegs: 5, ao: 0, autoFill: true,
              linkBorder: true, borderT: null, borderR: null, borderB: null, borderL: null,
              /* false = the border is part of the opening's fit; true = it is drawn
                 over an opening sized as if there were none. See buildRecessGeometry. */
              borderOverlay: false, borderDepth: null },
  /* Text effect sizes are a PERCENTAGE OF CAP HEIGHT, not metres. An outline at
     6% stays proportionally right when the type is resized, which is what every
     design tool does and what stops every effect needing re-tuning after a size
     change. The rasteriser already knows the cap height in pixels, so the
     conversion costs nothing.

     Flat rather than nested (fxShadow.dx and so on) so that a shallow
     { ...DEF.text, ...params } merge fills in every key on a document saved
     before these existed. */
  text:     { text: 'POP\nOUT', font: 'Chivo', weight: 900, capH: .9, depth: .35,
              bevel: .012, bevelSegs: 5, detail: 'normal', smooth: 35,
              align: 'center', lineHeight: 1.08, tracking: 0,           // em/1000 — 0 is normal setting
              mode: '3d',                                              // '3d' extruded | '2d' flat graphic
              fxOutline: false, fxOutlineW: 6, fxOutlineColor: '#000000',
              fxShadow: false, fxShadowDX: 4, fxShadowDY: -4, fxShadowBlur: 6,
              fxShadowColor: '#000000', fxShadowOpacity: .55,
              fxGlow: false, fxGlowSize: 12, fxGlowColor: '#F2C300', fxGlowOpacity: .8 },
  /* One text BOX rather than one string with mixed formatting: a paragraph is
     the unit that word-wraps, a run is the unit that's styled, and that split
     is what makes wrapping tractable at all. Flat only — see scene/textarea.js.
     `size` on a run is a PERCENTAGE of baseSize, so nudging baseSize (or
     resizing the box) scales every run together, the same relative-sizing
     convention capH already sets for 3D Text. */
  textarea: { w: 2.4, baseSize: .12, lineHeight: 1.3, padding: .04,
              paragraphs: [ { align: 'left', list: null, runs: [
                { text: 'Add your text here.', font: 'Inter', weight: 400, size: 180,
                  color: '#ffffff', italic: false, underline: false }
              ] } ],
              // Same fx keys, same percentages, same defaults as 3D Text's
              // capH-relative convention (see the comment above DEF.text) —
              // here they're a percentage of Base size instead, since a
              // Text Area has no single cap height. Painted into the same
              // canvas the text itself is drawn on (scene/textarea.js),
              // not a separate plane the way 3D Text's are.
              fxOutline: false, fxOutlineW: 6, fxOutlineColor: '#000000',
              fxShadow: false, fxShadowDX: 4, fxShadowDY: -4, fxShadowBlur: 6,
              fxShadowColor: '#000000', fxShadowOpacity: .55,
              fxGlow: false, fxGlowSize: 12, fxGlowColor: '#F2C300', fxGlowOpacity: .8 },
  /* A flat bezel: a solid rectangle with a rectangular hole punched out of it,
     the border widths adjustable per side exactly like a recess's — see
     recessBorders in scene/geometry.js, which both types share. Unlike a
     recess it has no depth or bevel: it is meant to sit as a thin plate just
     in front of the recess's own front face, not to be a container. */
  frame:    { w: 5.2, h: 2.8, border: .18,
              linkBorder: true, borderT: null, borderR: null, borderB: null, borderL: null },
  /* The same fx* block 3D Text and Text Area carry, and for the same reason —
     the effects are the object's, not the medium's. What a picture has instead
     of glyphs is its own silhouette; see geometry.js's buildBitmapEffects. */
  image:    { w: 2, cutoff: .02,
              fxOutline: false, fxOutlineW: 6, fxOutlineColor: '#000000',
              fxShadow: false, fxShadowDX: 4, fxShadowDY: -4, fxShadowBlur: 6,
              fxShadowColor: '#000000', fxShadowOpacity: .55,
              fxGlow: false, fxGlowSize: 12, fxGlowColor: '#F2C300', fxGlowOpacity: .8 },
  /* A clip on a plane. Sized like an image — width in metres, height from the
     source's own aspect — plus where it sits in time (see core/video-time.js
     for what offset/start/speed/loop mean).

     No `cutoff`. H.264 carries no alpha at all, and a clip that DOES have one
     (VP8/VP9 in WebM — see scene/video.js) carries a real matte, which blends;
     an alphaTest over either would only ever eat dark pixels and look like a
     bug. `srcAlpha` below says which kind this is.

     srcDur/srcW/srcH are CACHED metadata, not settings. An image can guess an
     aspect and reshape when the file decodes (scene/objects.js's placeholder
     0.6) because the only cost is a flicker; a video cannot, because the
     timing function is a pure function of these numbers and would otherwise
     answer differently before and after the first decode. They are read once
     at import and written into the document. */
  video:    { w: 2, depth: 0, bevel: .01,
              offset: 0, start: 0, speed: 1, loop: true,
              srcDur: 0, srcW: 0, srcH: 0, srcFps: 0, srcAlpha: false,
              fxOutline: false, fxOutlineW: 6, fxOutlineColor: '#000000',
              fxShadow: false, fxShadowDX: 4, fxShadowDY: -4, fxShadowBlur: 6,
              fxShadowColor: '#000000', fxShadowOpacity: .55,
              fxGlow: false, fxGlowSize: 12, fxGlowColor: '#F2C300', fxGlowOpacity: .8 },
  camera:   {},
  model:    {},
  /* A NULL, as in After Effects: a transform and nothing else. It is drawn as a
     marker in the workspace and never in a render, and exists to be a parent —
     link objects to it and moving, turning or scaling the null carries them. */
  null:     {},
  /* A BASIC SHAPE, built from numbers rather than imported from a file — a cube,
     a sphere, a cylinder. One bag of sizes serves every shape, each reading only
     the ones it has (see SHAPES), so switching a sphere to a cylinder in the
     inspector keeps a sensible size instead of starting from nothing. Metres,
     like everything else; `round` rounds the hard edges — the cube's corners,
     a cylinder's two rims, a cone's base rim. */
  shape:    { shape: 'box', w: 1, h: 1, d: 1, r: .5, tube: .15, round: .05 },
  light:    { kind: 'point', intensity: 30, angle: 34, penumbra: .45, distance: 0, decay: 2 }
};

/* The shapes, in the order the Add 3D Model dialog shows them, with the size
   fields each one actually uses. The inspector's Form tab and doc migration
   both read this, so a shape cannot grow a size the other does not know of. */
export const SHAPES = {
  box:      { label: 'Cube',     sizes: ['w', 'h', 'd', 'round'] },
  sphere:   { label: 'Sphere',   sizes: ['r'] },
  cylinder: { label: 'Cylinder', sizes: ['r', 'h', 'round'] },
  cone:     { label: 'Cone',     sizes: ['r', 'h', 'round'] },
  pyramid:  { label: 'Pyramid',  sizes: ['w', 'd', 'h'] },
  torus:    { label: 'Torus',    sizes: ['r', 'tube'] },
  capsule:  { label: 'Capsule',  sizes: ['r', 'h'] },
  plane:    { label: 'Plane',    sizes: ['w', 'h'] }
};

export const NICE = {
  camera: 'Camera', light: 'Light', recess: 'Inset', frame: 'Frame', model: '3D Model',
  shape: '3D Shape', null: 'Null',
  text: '3D Text', textarea: 'Text Area', image: 'Image', video: 'Video'
};

export const PALETTE = ['#F2C300', '#5BA9D6', '#E8EDF1', '#FF6B4A', '#4FD18B', '#C77DFF'];

/** A hair of overshoot so the recess border never leaves a sliver of background at the edge. */
export const overshoot = screen => Math.max(screen.w, screen.h) * 0.004;

/* The app's own placeholder screen (state/store.js's 9:16 default) is what
   every fixed metric default below was originally tuned against by eye.
   REF_M (its shorter side) is recessDefaults/frameDefaults' own existing
   scaling basis; REF_H (its full height) is what the light position
   formula in scene/objects.js's addObject already uses. Every *Defaults()
   function here reproduces its old fixed values exactly at this one
   screen size — the look never changes for a project already at the
   default, only for a real calibrated screen sized very differently from
   it (a 55" TV, say), which is the actual point of all of this. */
const REF_M = 3.375, REF_H = 6;

/**
 * A recess sized to the screen it will sit in. Separate from DEF.recess because
 * it depends on the calibrated screen dimensions, which DEF cannot see.
 *
 * Depth/border/bevel scale with the screen's shorter side again — fixed
 * metric defaults read fine at the app's own placeholder screen size but
 * absurdly thick or thin on a real calibrated screen far from that size in
 * either direction.
 */
export function recessDefaults(screen) {
  const W = screen.w, H = screen.h, m = Math.min(W, H), o = overshoot(screen);
  const border = m * (.4 / REF_M), bevel = m * (.02 / REF_M), depth = m * (2 / REF_M);
  return {
    w: Math.max(.02, W + 2 * o - 2 * (border + bevel)),
    h: Math.max(.02, H + 2 * o - 2 * (border + bevel)),
    depth, border, bevel, bevelSegs: 5, ao: 0, autoFill: true,
    linkBorder: true, borderT: null, borderR: null, borderB: null, borderL: null,
    borderOverlay: false, borderDepth: null
  };
}

/**
 * A frame sized to just cover the screen it will sit in front of — same
 * overshoot convention as recessDefaults, so the two line up by default.
 */
export function frameDefaults(screen) {
  const W = screen.w, H = screen.h, m = Math.min(W, H), o = overshoot(screen);
  return {
    w: W + 2 * o, h: H + 2 * o, border: m * .07,
    linkBorder: true, borderT: null, borderR: null, borderB: null, borderL: null
  };
}

/** 3D Text sized off the screen's shorter side, same REF_M basis as
 *  recessDefaults — capH alone; every fx* size is already a PERCENTAGE of
 *  capH (see DEF.text's own comment), so those scale automatically with it. */
export function textDefaults(screen) {
  const m = Math.min(screen.w, screen.h);
  return { ...DEF.text, capH: DEF.text.capH * (m / REF_M) };
}

/** Text Area sized off the screen's shorter side — box width, base size and
 *  padding all scale; lineHeight (a ratio) and every run's own `size`
 *  (already a PERCENTAGE of baseSize) do not need to. */
export function textareaDefaults(screen) {
  const m = Math.min(screen.w, screen.h), k = m / REF_M;
  return {
    ...DEF.textarea,
    w: DEF.textarea.w * k, baseSize: DEF.textarea.baseSize * k, padding: DEF.textarea.padding * k,
    paragraphs: DEF.textarea.paragraphs.map(p => ({ ...p, runs: p.runs.map(r => ({ ...r })) }))
  };
}

/** Image sized off the screen's shorter side — width only; height follows
 *  from the image's own aspect ratio once loaded (see scene/objects.js). */
export function imageDefaults(screen) {
  const m = Math.min(screen.w, screen.h);
  return { ...DEF.image, w: DEF.image.w * (m / REF_M) };
}

/** A clip lands at the same size an image would — the two are the same object
 *  as far as the scene is concerned, one just moves. The source metadata is
 *  filled in by the importer, which has already probed the file by the time
 *  the object exists (see scene/video-import.js). */
export function videoDefaults(screen) {
  const m = Math.min(screen.w, screen.h);
  return { ...DEF.video, w: DEF.video.w * (m / REF_M) };
}

/**
 * Light intensity, off the screen's HEIGHT rather than its shorter side —
 * matching addObject's own position formula (S.screen.h*.35), since this
 * has to track the same dimension that formula uses, not recessDefaults'.
 * Illuminance falls off with the square of distance, so keeping the same
 * apparent brightness at a light moved proportionally closer (a smaller
 * screen) needs intensity scaled by that ratio SQUARED, not linearly —
 * unlike every physical-size default above. angle/penumbra/decay are
 * angular or unitless and stay fixed regardless of screen size.
 */
/**
 * A shape sized off the screen's shorter side, the same basis the other
 * *Defaults use: about a third of it across, so a new shape is big enough to
 * read at a glance and small enough to fit inside the inset. Every size is
 * filled in, not only the chosen shape's, so switching shape later lands on a
 * matching size.
 */
export function shapeDefaults(screen, shape = 'box') {
  const b = Math.min(screen.w, screen.h) * .3;
  const kind = SHAPES[shape] ? shape : 'box';
  const p = { shape: kind, w: b, h: b, d: b, r: b / 2, tube: b * .14, round: b * .06 };
  if (kind === 'cylinder' || kind === 'capsule') p.r = b * .32;
  if (kind === 'cone') p.r = b * .45;
  if (kind === 'torus') p.r = b * .38;
  if (kind === 'plane') p.w = b * 1.4;
  return p;
}

export function lightDefaults(screen) {
  const refDist = Math.max(.3, REF_H * .35), dist = Math.max(.3, screen.h * .35);
  const ratio = dist / refDist;
  return { ...DEF.light, intensity: DEF.light.intensity * ratio * ratio };
}

/* ── serialise ───────────────────────────────────────────────────────────────
   `node` holds the live THREE.Object3D and must never reach JSON — it is
   circular and would be megabytes of nothing useful. */

/**
 * @param {object} state        the live S
 * @param {boolean} embedPhoto  inline the reference photo as a data URL
 * @param {number}  [v]         document version to stamp. Defaults to v1, the
 *                              self-contained shape a downloaded .json wants;
 *                              services/projects.js asks for v2 after it has
 *                              swapped the blobs out for asset ids.
 */
export function toDoc(state, embedPhoto, v = LEGACY_VERSION) {
  return {
    v,
    screen: state.screen,
    res: state.res,
    corners: state.corners,
    /* Deep-copied, unlike screen/res/corners above, which are written by
       reference — a pre-existing wart worth not extending. The shared corner
       edge is not here: it lives in `corners` above, where the first panel's
       right edge already is. */
    cornerPanel: state.cornerPanel ? {
      w: state.cornerPanel.w, resW: state.cornerPanel.resW,
      convex: state.cornerPanel.convex,
      outer: { tr: { ...state.cornerPanel.outer.tr }, br: { ...state.cornerPanel.outer.br } }
    } : null,
    /* Deep-copied for the same reason cornerPanel is. The wall it implies is
       NOT saved: screen/res above already hold it, and storing a derived
       rectangle beside the numbers it comes from is how the two drift.

       Two shapes, whitelisted separately — a free layout's fields would
       otherwise vanish through the grid's field list (cols: undefined, ...)
       and come back as an array with no screens in it. */
    screenArray: state.screenArray && state.screenArray.mode === 'free' ? {
      mode: 'free',
      screens: state.screenArray.screens.map(s => ({ ...s }))
    } : state.screenArray ? {
      cols: state.screenArray.cols, rows: state.screenArray.rows,
      gap: state.screenArray.gap,
      tile: { ...state.screenArray.tile }, res: { ...state.screenArray.res }
    } : null,
    focal: { mode: state.focal.mode, manualMM: state.focal.manualMM },
    eye: state.eye ? { x: state.eye.x, y: state.eye.y, z: state.eye.z } : null,
    cornersLocked: state.cornersLocked,
    nudge: state.nudge,
    solved: state.solved,
    bg: state.bg,
    tone: state.tone,
    envInt: state.envInt,
    aoStrength: state.aoStrength,
    aoReach: state.aoReach,
    dofStrength: state.dofStrength,
    dofFocus: state.dofFocus ? { ...state.dofFocus } : undefined,
    shadowRes: state.shadowRes,
    ss: state.ss,
    motionBlur: state.motionBlur,
    gizmoSize: state.gizmoSize,
    /* How the wall's margins are shown. The gap itself is in screenArray;
       these say what to draw in it. */
    showBezels: state.showBezels,
    bezelOpacity: state.bezelOpacity,
    bezelPhoto: state.bezelPhoto,
    onsite: state.onsite ? { ...state.onsite } : undefined,
    lighting: state.lighting,
    /* The sun's direction and the shadow blur. They were session state only —
       undo remembered them, the file did not, so a project reopened with the sun
       moved by hand came back at its preset's own direction. null is "use the
       preset's" and is kept as null. */
    lightAz: state.lightAz ?? null,
    lightEl: state.lightEl ?? null,
    shadowSoft: state.shadowSoft,
    /* Duration and fps only. The playhead is where someone happened to be
       looking when they saved, not a property of the work. */
    timeline: { duration: state.timeline.duration, fps: state.timeline.fps, loop: state.timeline.loop !== false },
    /* Uploaded faces travel with the project. Document level, not per object,
       because several text objects can share one. */
    fonts: (state.fonts || []).map(f => ({ ...f })),
    /* The crop travels even when the pixels do not. rect/rotated are four
       numbers and a flag, but core/calibrate.js needs them to place the
       principal point — without them a re-lock on a reopened project falls
       back to assuming the lens axis is centred, which for a cropped photo
       it is not. Dropping them was silently downgrading the solve. */
    photo: embedPhoto && state.photo ? state.photo
      : (state.photo ? { w: state.photo.w, h: state.photo.h,
                         rect: state.photo.rect, rotated: state.photo.rotated } : null),
    objects: state.objects.map(o => { const c = { ...o }; delete c.node; return c; })
  };
}

/* ── load ────────────────────────────────────────────────────────────────── */

/**
 * Validate and normalise a parsed document. Throws on something that clearly is
 * not an Illusio project; silently repairs anything that is merely old.
 */
export function fromDoc(doc) {
  if (!doc || typeof doc !== 'object' || !Array.isArray(doc.objects))
    throw new Error('That file isn’t a readable Illusio 3D project.');
  return migrate(doc);
}

/**
 * Repair documents written by older builds. Each fix is one shipped mistake:
 * cheap to keep, expensive to have removed by accident, so they live together
 * here rather than scattered through the loader.
 */
/* A corner panel is accepted only if it is COMPLETE, and otherwise dropped.
   Fails closed on purpose: every path downstream branches on
   `S.cornerPanel !== null`, so a half-valid one — a width but no marks, a mark
   with a NaN in it — would put the app into corner mode with geometry it cannot
   build, which is strictly worse than opening as the single screen the file
   otherwise describes. There is no partial state worth rescuing here. */
const mark = m => m && Number.isFinite(m.x) && Number.isFinite(m.y)
                    && m.x >= -1 && m.x <= 2 && m.y >= -1 && m.y <= 2;
export function normalizeCornerPanel(c) {
  if (!c || !(c.w > 0) || !Number.isFinite(c.w)) return null;
  const resW = Math.round(c.resW);
  if (!(resW >= 1) || !Number.isFinite(resW)) return null;
  if (!c.outer || !mark(c.outer.tr) || !mark(c.outer.br)) return null;
  return {
    w: Math.min(500, c.w), resW: Math.min(16384, resW),
    convex: c.convex !== false,
    outer: { tr: { x: +c.outer.tr.x, y: +c.outer.tr.y },
             br: { x: +c.outer.br.x, y: +c.outer.br.y } }
  };
}

/* A free layout, screen by screen. Same contract as the grid below: a
   half-valid array is rejected outright rather than half-honoured, because
   there is no correct way to silently drop one screen out of a wall someone
   built and keep going — the rest would shift in the export they get,
   silently, which is worse than the array failing to open at all. */
function normalizeFreeArray(a) {
  if (!Array.isArray(a.screens) || !a.screens.length) return null;
  if (a.screens.length > MAX_TILES) return null;
  const seen = new Set();
  const out = [];
  for (let i = 0; i < a.screens.length; i++) {
    const s = a.screens[i] || {};
    const w = +s.w, h = +s.h, x = +s.x, y = +s.y;
    const resW = Math.round(s.resW), resH = Math.round(s.resH);
    if (!(w > 0) || !(h > 0) || !Number.isFinite(x) || !Number.isFinite(y)) return null;
    if (!(resW >= 1) || !(resH >= 1) || !Number.isFinite(resW) || !Number.isFinite(resH)) return null;
    /* A short, unique label — what the exported files and the README are
       named by, so it has to survive being read back a year later. Falls
       back to a letter by POSITION in the array rather than by index into
       `out`, so a screen that failed validation above (there is none, this
       function rejects the whole thing) could never shift every later
       screen's default letter — kept this way regardless, since a letter that
       depends on something other than "which screen is this" is a bug
       waiting to happen the next time this function changes. */
    let key = (typeof s.key === 'string' && s.key.trim()) ? s.key.trim().slice(0, 12)
                                                            : String.fromCharCode(65 + i);
    while (seen.has(key)) key += "'";      // de-duplicate rather than reject
    seen.add(key);
    out.push({ key, w: Math.min(500, w), h: Math.min(500, h), x, y,
               resW: Math.min(16384, resW), resH: Math.min(16384, resH) });
  }
  return { mode: 'free', screens: out };
}

/* Same contract as normalizeCornerPanel: a half-valid array is rejected
   outright rather than half-honoured, because every consumer branches on
   `S.screenArray !== null` and a grid with no tile size would derive a wall of
   NaN. Capped so a corrupt file cannot ask for a million cameras. */
export function normalizeScreenArray(a) {
  if (!a) return null;
  if (a.mode === 'free') return normalizeFreeArray(a);
  const cols = Math.round(a.cols), rows = Math.round(a.rows);
  if (!(cols >= 1) || !(rows >= 1) || !Number.isFinite(cols) || !Number.isFinite(rows)) return null;
  if (cols * rows > MAX_TILES) return null;
  const tw = +a.tile?.w, th = +a.tile?.h, rw = Math.round(a.res?.w), rh = Math.round(a.res?.h);
  if (!(tw > 0) || !(th > 0) || !Number.isFinite(tw) || !Number.isFinite(th)) return null;
  if (!(rw >= 1) || !(rh >= 1) || !Number.isFinite(rw) || !Number.isFinite(rh)) return null;
  const gap = +a.gap;
  return {
    cols, rows,
    gap: Number.isFinite(gap) && gap > 0 ? Math.min(50, gap) : 0,
    tile: { w: Math.min(500, tw), h: Math.min(500, th) },
    res: { w: Math.min(16384, rw), h: Math.min(16384, rh) }
  };
}

export function migrate(doc) {
  const d = { ...doc };
  const D = projectDefaults();

  d.screen = d.screen || D.screen;
  d.res = d.res || D.res;
  d.corners = d.corners || D.corners;
  d.cornerPanel = normalizeCornerPanel(d.cornerPanel);
  d.screenArray = normalizeScreenArray(d.screenArray);
  // Exclusive, and enforced on the way IN as well as in the UI: a file
  // hand-edited to carry both would otherwise render a corner and export a
  // grid. The corner is the older feature, so it wins.
  if (d.cornerPanel) d.screenArray = null;
  d.focal = { mode: d.focal?.mode || D.focalMode, manualMM: d.focal?.manualMM || D.focalManualMM };
  d.nudge = d.nudge || D.nudge;
  d.solved = !!d.solved;
  /* A SCREEN ARRAY WITH A HAND-SET VIEWER CANNOT BE STALE, so a file saying it
     is has been damaged rather than described. Its photo places a mockup and
     nothing else — no solve runs on that path (calibrate-view's placementOnly)
     and the footer deliberately offers no Lock angle — so "the corners moved,
     lock the angle again" has no meaning there and nothing in the UI could ever
     clear it. A wall calibrated from its photo has no camera object and can
     honestly be stale, exactly as a single screen can.

     A released build wrote exactly that: onScreenChange marked any solved
     project WITH A PHOTO stale when the screen changed, and an array's
     placement photo satisfied it, so editing the grid disabled Export for good
     and saved the flag into the file. Guarding onScreenChange stops it
     happening again but cannot reach a project already carrying it, and the
     project cannot repair itself, so the repair happens here on the way in.

     Conditioned on a viewpoint existing: an array that was never set up has no
     eye, and there solved:false is the truth rather than the damage. */
  if (d.screenArray && d.eye && (d.objects || []).some(o => o && o.type === 'camera')) d.solved = true;
  d.cornersLocked = !!d.cornersLocked;
  d.bg = d.bg || D.bg;
  d.tone = d.tone || D.tone;
  d.envInt = d.envInt ?? D.envInt;
  /* Absent from every project saved before occlusion existed — they land on
     0, which is exactly how they rendered. */
  d.aoStrength = d.aoStrength != null && Number.isFinite(+d.aoStrength)
    ? Math.min(1, Math.max(0, +d.aoStrength)) : D.aoStrength;
  d.aoReach = d.aoReach != null && Number.isFinite(+d.aoReach) && +d.aoReach > 0
    ? Math.min(2, Math.max(.01, +d.aoReach)) : D.aoReach;
  /* Absent before depth of field existed: those projects land on 0, sharp, as
     they always rendered. A focus that names an object keeps its id even if
     that object is gone; the renderer falls back to the screen surface. */
  d.dofStrength = d.dofStrength != null && Number.isFinite(+d.dofStrength)
    ? Math.min(1, Math.max(0, +d.dofStrength)) : D.dofStrength;
  {
    const f = d.dofFocus && typeof d.dofFocus === 'object' ? d.dofFocus : {};
    const mode = ['screen', 'custom', 'object'].includes(f.mode) ? f.mode : 'screen';
    d.dofFocus = {
      mode,
      z: Number.isFinite(+f.z) ? +f.z : 0,
      id: mode === 'object' && Number.isFinite(+f.id) ? +f.id : null
    };
    if (mode === 'object' && d.dofFocus.id == null) d.dofFocus.mode = 'screen';
  }
  d.shadowRes = d.shadowRes || D.shadowRes;
  d.ss = d.ss ?? D.ss;
  /* Clamped to the set the exporter actually offers rather than trusted: this
     is a direct multiplier on export time, so a hand-edited 500 would be a
     render nobody sits through. Anything written before motion blur existed
     lands on 1, which is off. */
  d.motionBlur = Math.min(16, Math.max(1, Math.round(+d.motionBlur || D.motionBlur)));
  d.gizmoSize = d.gizmoSize ?? D.gizmoSize;
  /* Absent on every project saved before these travelled with one, which is
     the common case for a while yet — the defaults are what those files were
     already being shown with, so they open unchanged. Opacity is clamped
     rather than trusted: it multiplies a material's alpha, where a
     hand-edited 40 would simply be wrong. */
  d.showBezels = d.showBezels !== false;
  d.bezelOpacity = Number.isFinite(+d.bezelOpacity)
    ? Math.min(1, Math.max(0, +d.bezelOpacity)) : D.bezelOpacity;
  d.bezelPhoto = d.bezelPhoto === true;
  /* Absent before the on-site choices were saved: those projects open with the defaults, as they always
     did. Each is clamped to what the exporter accepts (export/onsite.js's FLAT_*_MAX and RESOLVE
     rules clamp again at render time), because a hand-edited value must not reach a render. */
  {
    const o = d.onsite && typeof d.onsite === 'object' ? d.onsite : {};
    const pct = (v, max) => Number.isFinite(+v) ? Math.min(max, Math.max(0, +v)) : 0;
    const secs = v => v != null && Number.isFinite(+v) ? Math.min(3600, Math.max(0, +v)) : null;
    d.onsite = {
      resolveToFlat: o.resolveToFlat === true,
      resolveAt: secs(o.resolveAt), stopsAt: secs(o.stopsAt),
      flatMargin: pct(o.flatMargin, 30), flatGap: pct(o.flatGap, 15),
      useFilmed: o.useFilmed !== false
    };
  }
  d.lighting = d.lighting || D.lighting;
  /* Absent before they were saved: those projects land on the preset's own
     direction (null) and the default blur, which is exactly how they opened. */
  d.lightAz = d.lightAz != null && Number.isFinite(+d.lightAz) ? Math.min(360, Math.max(0, +d.lightAz)) : null;
  d.lightEl = d.lightEl != null && Number.isFinite(+d.lightEl) ? Math.min(89, Math.max(1, +d.lightEl)) : null;
  d.shadowSoft = d.shadowSoft != null && Number.isFinite(+d.shadowSoft)
    ? Math.min(20, Math.max(0, +d.shadowSoft)) : D.shadowSoft;
  /* Clamped rather than trusted: a duration of 0 divides by zero in the
     timeline's own maths, and an fps of 1000 would ask the exporter for a
     hundred thousand frames. Every project written before the timeline
     existed lands on the defaults here. */
  d.timeline = {
    duration: Math.min(600, Math.max(.1, +d.timeline?.duration || D.timeline.duration)),
    fps: Math.min(120, Math.max(1, Math.round(+d.timeline?.fps || D.timeline.fps))),
    // absent (every project written before this) means the old behaviour, which was to loop
    loop: d.timeline?.loop !== false
  };

  /* Fonts the document brings with it. Normalised before the objects, because
     resolving each text object's family needs to know which uploaded families
     this document actually carries. */
  d.fonts = (Array.isArray(d.fonts) ? d.fonts : [])
    .filter(f => f && f.family)
    .map(f => ({ family: String(f.family), weight: +f.weight || 400, src: f.src, assetId: f.assetId }));
  const docFamilies = d.fonts.map(f => f.family);

  d.objects = (d.objects || []).map(raw => {
    const o = { ...raw };

    if (typeof o.scale === 'number') o.scale = { x: o.scale, y: o.scale, z: o.scale };
    if (!o.scale) o.scale = { x: 1, y: 1, z: 1 };
    if (o.lockScale === undefined) o.lockScale = true;

    /* Where rotation and scale happen from, in bounding-box units: -1 is one
       face, +1 the opposite, 0 the middle of that axis. Normalised rather
       than metres so a preset stays put when the object is resized — "bottom
       centre" is the bottom whatever height the box is.

       ABSENT means the geometry's own origin, which is what every object
       built before this existed used. That is NOT the same as {0,0,0}: a box
       is modelled around its centre but 3D Text is built around its
       baseline, so defaulting a document to the bbox centre would visibly
       shift every text object the first time it was opened. Undefined stays
       undefined, and only an explicit choice moves anything. */
    o.pivot = vec3In(o.pivot, -1, 1);
    if (!o.pivot) delete o.pivot;

    /* Fill in params added since this document was written. Without it a project
       saved before text effects existed opens with every fx* key undefined, and
       every builder has to guess whether undefined means off or means default.
       Builders still read defensively — this is belt and braces, and it is also
       what makes adding a param in future a one-line change. */
    if (DEF[o.type]) o.params = { ...DEF[o.type], ...(o.params || {}) };

    /* A shape is built straight from these numbers, so a hand-edited or corrupt
       one is repaired here rather than handed to three.js: an unknown shape
       becomes a cube, and a size that is not a positive number falls back to
       the default. The corner radius may be 0. */
    if (o.type === 'shape') {
      const P = o.params;
      if (!SHAPES[P.shape]) P.shape = 'box';
      for (const k of ['w', 'h', 'd', 'r', 'tube'])
        P[k] = Number.isFinite(+P[k]) && +P[k] > 0 ? +P[k] : DEF.shape[k];
      P.round = Number.isFinite(+P.round) && +P.round >= 0 ? +P.round : DEF.shape.round;
    }

    /* System fonts are gone. They were never portable: the geometry is traced
       from whatever face the machine has, so Impact on a Mac and Impact on
       Windows are different meshes and therefore different deliverables. Map
       each old CSS stack to the closest bundled family so an existing project
       opens with type of similar weight and proportion rather than silently
       falling back. */
    if (o.type === 'text') {
      o.params.font = resolveFamily(o.params.font, docFamilies);
      /* And snap the weight. A face only ships certain weights; asking for one
         it does not have gets a SYNTHESISED face — strokes thickened by the
         rasteriser rather than drawn — and that synthesis is what gets traced.
         The old menu offered Chivo Light while only 400/700/900 were loaded, so
         projects carrying weight 300 exist. Uploaded families are left alone:
         their weights are whatever was uploaded, not something we can know. */
      const ws = builtInWeights(o.params.font);
      if (ws) o.params.weight = snapWeight(ws, o.params.weight);
      else o.params.weight = +o.params.weight || 400;
      o.params.tracking = +o.params.tracking || 0;
    }

    /* Text Area: same family/weight repair, per run rather than once. A
       paragraph list that isn't a well-formed array of runs is dropped rather
       than trusted — the word-wrap engine three layers down should never have
       to guess what a corrupt or hand-edited shape meant. */
    if (o.type === 'textarea') {
      const fixRun = r => {
        const font = resolveFamily(r && r.font, docFamilies);
        const rws = builtInWeights(font);
        const weight = rws ? snapWeight(rws, r && r.weight) : (+(r && r.weight) || 400);
        return {
          text: String(r && r.text != null ? r.text : ''),
          font, weight,
          size: +(r && r.size) || 100,
          color: (r && r.color) || '#ffffff',
          italic: !!(r && r.italic),
          underline: !!(r && r.underline)
        };
      };
      const paras = Array.isArray(o.params.paragraphs) ? o.params.paragraphs : [];
      const fixed = paras
        .filter(pa => pa && Array.isArray(pa.runs) && pa.runs.length)
        .map(pa => ({
          align: ['left','center','right','justify'].includes(pa.align) ? pa.align : 'left',
          list: pa.list === 'bullet' || pa.list === 'number' ? pa.list : null,
          lineHeight: pa.lineHeight == null ? null : +pa.lineHeight,
          runs: pa.runs.map(fixRun)
        }));
      o.params.paragraphs = fixed.length ? fixed : DEF.textarea.paragraphs.map(pa => ({ ...pa, runs: pa.runs.map(r => ({...r})) }));
      /* The word reveal, as saved — the frame the playhead happened to be on,
         exactly like a saved pos, and overwritten by the track on the next
         evaluation. Absent or unreadable becomes 1 rather than 0: both mean
         "we do not know how much to show", and the failure that shows all the
         text is recoverable where the one that shows none looks like data
         loss. */
      const rv = +o.params.reveal;
      o.params.reveal = Number.isFinite(rv) ? Math.min(1, Math.max(0, rv)) : 1;
      /* Its twin, and the inert value is the other end: none of the block has
         left. Same reasoning about a corrupt value — 0 here is what shows the
         text, and that is the recoverable failure. */
      const er = +o.params.erase;
      o.params.erase = Number.isFinite(er) ? Math.min(1, Math.max(0, er)) : 0;
    }

    /* A clip's timing is arithmetic that runs once per object per frame, and
       core/video-time.js is deliberately defenceless — it is a pure function,
       not a validator. Clamp here instead, once, on the way in: a hand-edited
       negative speed would otherwise divide time backwards, and a NaN offset
       would put the plane nowhere at all. */
    if (o.type === 'video') {
      const P = o.params;
      P.depth = Math.max(0, +P.depth || 0);
      P.bevel = Math.max(0, +P.bevel || 0);
      P.offset = Number.isFinite(+P.offset) ? +P.offset : 0;
      P.start = Math.max(0, +P.start || 0);
      P.speed = Math.min(8, Math.max(.05, +P.speed || 1));
      P.loop = P.loop !== false;
      P.srcDur = Math.max(0, +P.srcDur || 0);
      P.srcW = Math.max(0, Math.round(+P.srcW || 0));
      P.srcH = Math.max(0, Math.round(+P.srcH || 0));
      /* Cached for the readout and the rate check only — nothing about
         playback consults it, because sampling is done on a continuous time
         axis (see core/video-time.js). 0 means "not probed", which is what
         every clip imported before this carries. */
      P.srcFps = Math.max(0, Math.min(240, +P.srcFps || 0));
      /* Cached like the three above it: measured off the file at import, not
         chosen. A document written before transparent clips existed has no
         value here and lands on false, which is what those clips were. */
      P.srcAlpha = !!P.srcAlpha;
    }

    /* Keyframes, repaired the same way params are. normalizeAnim drops
       malformed keys and returns undefined when nothing usable is left, so an
       object that was never animated carries no empty `anim` object into
       every subsequent save. */
    const anim = normalizeAnim(o.anim);
    if (anim) o.anim = anim; else delete o.anim;

    o.node = null;
    return o;
  }).filter(o => DEF[o.type]);            // unknown type — nothing could be built from it

  if (d.selId != null && !d.objects.some(o => o.id === d.selId)) d.selId = null;
  // The version is preserved, not stamped: a v1 document that still carries data
  // URLs is not a v2 document just because it has been normalised.
  d.v = doc.v === DOC_VERSION ? DOC_VERSION : LEGACY_VERSION;
  return d;
}

/** The id counter must clear anything restored from a document. */
export const nextIdFor = objects => Math.max(1, ...objects.map(o => o.id || 0)) + 1;

/* ── asset references ───────────────────────────────────────────────────────
   The three places a blob can be referenced. Everything that converts between v1
   and v2 goes through this one walker, so no caller has to know the document
   shape — and adding a fourth kind of asset later means changing one function. */

/**
 * Visit every asset reference in a document.
 *
 * `holder` is the object the reference lives on, so a caller can read the id and
 * the data URL as two separate fields — which is what lets a loaded v2 document
 * carry a signed URL in `src` while `assetId` stays the thing that gets saved.
 *
 * @param {object} doc
 * @param {(ref:{kind:string, holder:object, srcKey:string}) => void} fn
 */
export function walkAssets(doc, fn) {
  if (doc.photo) fn({ kind: 'photo', holder: doc.photo, srcKey: 'url' });
  /* Uploaded font files are assets like any other blob, so v1→v2 uploads them,
     hydrate signs them and dehydrate strips them with no new machinery. A font
     binary sitting inline in a jsonb column is exactly what doc v2 exists to
     prevent. */
  for (const f of doc.fonts || []) fn({ kind: 'font', holder: f, srcKey: 'src' });
  for (const o of doc.objects || []) {
    if (o.params && (o.type === 'image' || o.type === 'model'))
      fn({ kind: o.type === 'model' ? 'model' : 'texture', holder: o.params, srcKey: 'src' });
    /* Video gets its own kind rather than reusing 'texture'. upload() dedupes
       on (owner, checksum, kind), and more to the point `kind` is what any
       future storage-quota, cleanup job or admin view keys on — "how much
       video is this account holding" is a question worth being able to ask. */
    if (o.params && o.type === 'video')
      fn({ kind: 'video', holder: o.params, srcKey: 'src' });
    if (o.map) fn({ kind: 'texture', holder: o.map, srcKey: 'src' });
  }
}

/**
 * True if the document references bytes that are not in cloud storage yet —
 * i.e. saving it as-is would write a row pointing at nothing.
 *
 * Two shapes qualify, and missing the second one is a silent data-loss bug:
 * an inline `data:` URL (an image or a model, still heavy), and a `blobId`
 * with no `assetId` (a clip picked while signed out, living in this browser's
 * IndexedDB and nowhere else). The second cannot be spotted by looking at
 * `src` at all, which is why this is no longer called hasInlineAssets.
 */
export function hasUnuploadedAssets(doc) {
  let found = false;
  walkAssets(doc, ({ holder, srcKey }) => {
    const v = holder[srcKey];
    /* Bytes AND NO ID — the same shape as the blobId clause beside it. Holding
       bytes is not the question; whether they have been put somewhere is. */
    if (typeof v === 'string' && v.startsWith('data:') && !holder.assetId) found = true;
    if (holder.blobId && !holder.assetId) found = true;
  });
  return found;
}

/** Every assetId a v2 document points at. */
export function assetIdsIn(doc) {
  const ids = [];
  walkAssets(doc, ({ holder }) => { if (holder.assetId) ids.push(holder.assetId); });
  return [...new Set(ids)];
}

/**
 * v1 → v2. `uploader(dataUrl, kind, holder)` returns an asset id.
 *
 * The data URL is dropped once it has an id: leaving it in place would defeat
 * the entire point, since the document is about to be written to a jsonb column.
 * Progress is reported because a project with a model in it will take a moment
 * and a frozen dialog reads as a crash.
 *
 * TWO KINDS OF PENDING BLOB, and the second is easy to miss. An image or a
 * model arrives as a `data:` URL and is recognisable by looking at `src`. A
 * video never does — it goes straight into local IndexedDB and leaves only a
 * `blobId` behind (see services/media-store.js), because a clip inlined as
 * base64 would be re-serialised in full on every local save. Those refs are
 * enqueued too, with `dataUrl` passed as null, and the uploader resolves the
 * bytes from the blob store instead. Without this branch a clip picked while
 * signed out would silently never upload, and the cloud row would point at a
 * blob that exists in exactly one browser.
 *
 * @param {object} doc      mutated in place
 * @param {(dataUrl:string|null, kind:string, holder:object) => Promise<string>} uploader
 * @param {(done:number, total:number) => void} [onProgress]
 */
export async function toV2(doc, uploader, onProgress) {
  const jobs = [];
  walkAssets(doc, ref => {
    const v = ref.holder[ref.srcKey];
    if (typeof v === 'string' && v.startsWith('data:') && !ref.holder.assetId) jobs.push(ref);
    else if (ref.holder.blobId && !ref.holder.assetId) jobs.push(ref);
  });

  let done = 0;
  onProgress?.(0, jobs.length);
  for (const ref of jobs) {
    const v = ref.holder[ref.srcKey];
    const dataUrl = typeof v === 'string' && v.startsWith('data:') ? v : null;
    ref.holder.assetId = await uploader(dataUrl, ref.kind, ref.holder);
    /* THE ID IS WRITTEN BACK, THE BYTES ARE LEFT ALONE. These holders are the
       LIVE SCENE's — toDoc shares params and map rather than copying them —
       so the id landing here is how the scene learns it no longer holds
       unuploaded bytes, and that part is deliberate.

       Deleting the src was not. It blinded the scene: the picture kept drawing
       from the texture its material already held, and vanished the moment
       anything rebuilt the node, because buildImage opens with
       `if(!o.params.src) return g` and hands back an empty group — no mesh, no
       material, no bounds. Reported as an image disappearing on Flat colour,
       which is simply the first thing that rebuilds, and coming back when the
       project is reopened, because hydrate puts a signed URL where the bytes
       used to be. dehydrate strips src for the FILE, from copies, which is
       where stripping belongs; nothing needed it stripped from the scene.

       What the delete was really carrying is "these bytes are uploaded now",
       so both predicates say that instead — bytes and no id. */
    onProgress?.(++done, jobs.length);
  }
  doc.v = DOC_VERSION;
  return doc;
}

/**
 * Put URLs back where the renderer expects them.
 *
 * The rest of the app never learned about asset ids — getTexture, buildGLBNode
 * and the four live <img> elements all still read a plain URL — so a loaded v2
 * document is rehydrated here and everything downstream is unchanged. assetId
 * stays alongside, because that is what gets saved.
 *
 * @param {object} doc              mutated in place
 * @param {Map<string,string>} urls assetId → signed URL
 */
export function hydrate(doc, urls) {
  walkAssets(doc, ({ holder, srcKey }) => {
    if (holder.assetId && urls.has(holder.assetId)) holder[srcKey] = urls.get(holder.assetId);
  });
  return doc;
}

/** Strip the resolved URLs again before saving, keeping only the ids.
 *
 *  `blobId` goes with them once an `assetId` exists: it names a record in one
 *  browser's IndexedDB, so leaving it in a shared jsonb row would be a pointer
 *  that resolves to nothing on every other machine — and worse, one that
 *  hasUnuploadedAssets would have to keep ignoring by exception. */
export function dehydrate(doc) {
  /* STRIP FROM COPIES, because the doc does not own what it is holding.
     toDoc shallow-copies each object and passes `photo` straight through, so
     `params`, `map` and `photo` are THE SAME OBJECTS the live scene is using.
     Deleting `src` from them therefore blinded the scene itself: after any
     cloud save — autosave included — an image, video or model still drew from
     the texture its material already held, and vanished the moment anything
     rebuilt it. buildImage opens with `if(!o.params.src) return g`, so the
     rebuild produced an empty group: no mesh, no material, no bounds. Reported
     as an image going transparent after clicking Flat colour, which is simply
     the first thing that rebuilds. Nothing brought it back, because the source
     was gone from the scene AND from the file that had just been written.

     The copies are made HERE rather than in toDoc on purpose: toV2 runs before
     this and writes freshly uploaded asset ids back through those same shared
     references, which is how the live scene learns it no longer holds
     unuploaded bytes. Copying earlier would silently cost a re-upload of every
     asset on every save. */
  doc.objects = (doc.objects || []).map(o => {
    const c = { ...o };
    if (c.params) c.params = { ...c.params };
    if (c.map) c.map = { ...c.map };
    return c;
  });
  if (doc.photo) doc.photo = { ...doc.photo };
  doc.fonts = (doc.fonts || []).map(f => ({ ...f }));

  walkAssets(doc, ({ holder, srcKey }) => {
    if (holder.assetId) { delete holder[srcKey]; delete holder.blobId; }
  });
  return doc;
}
